import React, { useState, useEffect, useRef } from 'react';
import { FaUsers, FaComments, FaEdit, FaSave, FaTimes, FaUserPlus, FaUserCheck } from 'react-icons/fa';
import '../css/Chat.css';
import { authAPI } from '../api/api';
import chatAPI from '../api/chatAPI';
import { useChat } from '../context/ChatContext';

export default function ChatCreateModal({ onClose, onCreate }) {
  const { createChat } = useChat();
  const [name, setName] = useState('');
  const [users, setUsers] = useState([]);
  const [desc, setDesc] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchCompanyUsers();
  }, []);

  const fetchCompanyUsers = async () => {
    try {
      setLoading(true);
      const usersData = await chatAPI.getClientUsers();
      const currentUser = authAPI.getCurrentUser();

      // Kiszűrjük a jelenlegi felhasználót és csak client usereket tartunk meg
      const filteredUsers = usersData ? usersData.filter(user => {
        // Csak client userek (saját céges kollégák)
        if (user.user_type !== 'client') {
          return false;
        }
        // Kizárjuk saját magunkat
        if (user.id === currentUser?.id || user.id === currentUser?.user_id) {
          return false;
        }
        return true;
      }) : [];

      // Csoportosítás user_type szerint (de most csak client lesz)
      const grouped = {
        backend: [], // Nem jelenítjük meg a backend usereket
        client: filteredUsers
      };

      setCompanyUsers(grouped);
    } catch (error) {
      console.error('Error fetching company users:', error);
      setCompanyUsers({ backend: [], client: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleUserToggle = (user) => {
    // Create unique key: type-id (e.g., "backend-1", "client-2")
    const userKey = `${user.user_type}-${user.id}`;
    setUsers(prev => prev.includes(userKey) ? prev.filter(u => u !== userKey) : [...prev, userKey]);
  };

  // Flatten the grouped users for selected users display
  const allUsers = [...(companyUsers.backend || []), ...(companyUsers.client || [])];
  const selectedUsers = allUsers.filter(u => {
    const userKey = `${u.user_type}-${u.id}`;
    return users.includes(userKey);
  });

  const handleSave = async () => {
    if (!name.trim() || users.length === 0 || saving) return;

    try {
      setSaving(true);

      // Extract user IDs from the unique keys (format: "type-id")
      const userIds = users.map(userKey => {
        const [type, id] = userKey.split('-');
        return parseInt(id);
      });

      await createChat({
        name: name.trim(),
        desc: desc.trim(),
        users: userIds
      });

      // Sikeres létrehozás esetén modal bezárása
      onCreate && onCreate({ name, users: userIds, desc });
    } catch (error) {
      console.error('Chat létrehozási hiba:', error);
      alert('Hiba történt a chat létrehozása során: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="chat-modal-backdrop" onClick={handleClose}>
      <div className="chat-modal" onClick={e => e.stopPropagation()}>
        <div className="chat-modal-header">
          <div className="chat-modal-title">
            <FaComments className="chat-modal-icon" />
            <h3>Chatszoba létrehozása</h3>
          </div>
          <button className="chat-modal-close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="chat-modal-content">
          <div className="chat-modal-section">
            <label className="chat-modal-label">
              <FaEdit className="label-icon" />
              Elnevezés
            </label>
            <input 
              className="chat-modal-input" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              placeholder="Adja meg a chatszoba nevét..." 
            />
        </div>

          <div className="chat-modal-section">
            <label className="chat-modal-label">
              <FaUsers className="label-icon" />
              Részvevők ({selectedUsers.length})
            </label>
        <div className="user-dropdown-wrapper" ref={dropdownRef}>
          <div className="user-dropdown-selected" onClick={() => setDropdownOpen(v => !v)}>
            {selectedUsers.length === 0 ? (
                  <span className="user-dropdown-placeholder">
                    <FaUserPlus className="placeholder-icon" />
                    Válassza ki a résztvevőket...
                  </span>
            ) : (
                  <div className="selected-users-container">
                    {selectedUsers.map(u => {
                      const userKey = `${u.user_type}-${u.id}`;
                      return (
                        <span className="user-dropdown-tag" key={userKey}>
                          <FaUserCheck className="tag-icon" />
                          {u.name}
                          <span
                            className="user-dropdown-tag-x"
                            onClick={e => {
                              e.stopPropagation();
                              setUsers(prev => prev.filter(key => key !== userKey));
                            }}
                          >
                            <FaTimes />
                          </span>
                        </span>
                      );
                    })}
                  </div>
            )}
            <span className="user-dropdown-arrow">▼</span>
          </div>
          {dropdownOpen && (
            <div className="user-dropdown-list">
                  {loading ? (
                    <div className="user-dropdown-loading">
                      <div className="loading-spinner"></div>
                      Felhasználók betöltése...
                    </div>
                  ) : allUsers.length === 0 ? (
                    <div className="user-dropdown-empty">
                      <FaUsers className="empty-icon" />
                      Nincs elérhető felhasználó
                    </div>
              ) : (
                    <>
                      {/* Backend Users */}
                      {companyUsers.backend && companyUsers.backend.length > 0 && (
                        <>
                          <div className="user-dropdown-group-header">
                            <FaUserCheck className="group-icon" />
                            Adminok
                          </div>
                          {companyUsers.backend.map(u => {
                            const userKey = `${u.user_type}-${u.id}`;
                            const isSelected = users.includes(userKey);
                            return (
                              <div
                                key={userKey}
                                className={`user-dropdown-item${isSelected ? ' selected' : ''}`}
                                onClick={() => handleUserToggle(u)}
                              >
                                <div className="user-item-info">
                                  <span className="user-name">{u.name}</span>
                                  {u.email && <span className="user-email">{u.email}</span>}
                                </div>
                                {isSelected ? (
                                  <FaUserCheck className="user-dropdown-check" />
                                ) : null}
                              </div>
                            );
                          })}
                        </>
                      )}

                      {/* Client Users */}
                      {companyUsers.client && companyUsers.client.length > 0 && (
                        <>
                          <div className="user-dropdown-group-header">
                            <FaUsers className="group-icon" />
                            Kollégák
                          </div>
                          {companyUsers.client.map(u => {
                            const userKey = `${u.user_type}-${u.id}`;
                            const isSelected = users.includes(userKey);
                            return (
                              <div
                                key={userKey}
                                className={`user-dropdown-item${isSelected ? ' selected' : ''}`}
                                onClick={() => handleUserToggle(u)}
                              >
                                <div className="user-item-info">
                                  <span className="user-name">{u.name}</span>
                                  {u.email && <span className="user-email">{u.email}</span>}
                                </div>
                                {isSelected ? (
                                  <FaUserCheck className="user-dropdown-check" />
                                ) : null}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </>
              )}
            </div>
          )}
        </div>
          </div>

          <div className="chat-modal-section">
            <label className="chat-modal-label">
              <FaEdit className="label-icon" />
              Leírás (opcionális)
            </label>
            <textarea 
              className="chat-modal-textarea" 
              value={desc} 
              onChange={e => setDesc(e.target.value)} 
              placeholder="Adja meg a chatszoba leírását..." 
              rows={3} 
            />
          </div>
        </div>

        <div className="chat-modal-actions">
          <button className="chat-modal-cancel-btn" onClick={handleClose}>
            <FaTimes />
            Mégse
          </button>
          <button 
            className="chat-modal-save-btn" 
            onClick={handleSave} 
            disabled={!name.trim() || users.length === 0 || saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner" />
                Létrehozás...
              </>
            ) : (
              <>
                <FaSave />
                Létrehozás
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
} 